// ============================================================
// Conventions collectives — interrogation des bases de l'ÉTAT
// ============================================================
//
// Deux sources, deux usages complémentaires :
//
//   • LE CATALOGUE (hr-idcc-catalog.ts) — la base KALI de la DILA, embarquée. Elle répond à
//     « quel est le titre officiel de l'IDCC 1982, et où lire le texte ? ». Hors ligne, instantanée,
//     et c'est elle qui porte l'identifiant KALICONT d'où sort le lien Légifrance.
//
//   • L'ANNUAIRE DES ENTREPRISES (ici) — l'API publique `recherche-entreprises.api.gouv.fr`,
//     alimentée par l'INSEE et les DSN. Elle répond à l'autre question, la vraie : « quelle
//     convention MON entreprise déclare-t-elle ? ». On lui donne un SIRET, un SIREN ou une
//     raison sociale, elle rend les IDCC effectivement déclarés pour l'établissement.
//
// POURQUOI PAS `HttpClient` TEL QUEL — l'`AuthInterceptor` de l'application colle le JWT
// Mercasell sur TOUTE requête sortante. Ici, cela ferait deux dégâts : envoyer le jeton de
// session à un tiers, et déclencher un pré-vol CORS que l'API refuse (elle n'autorise que
// l'en-tête `Content-Type`). On passe donc par `HttpBackend`, qui court-circuite les
// intercepteurs : cet appel part sans en-tête d'authentification, comme il se doit.

import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { IdccEntry, hrIdccOf } from './hr-idcc-catalog';

// Un établissement trouvé dans l'annuaire, avec les conventions qu'il déclare.
export interface CompanyIdccMatch {
	name: string;						// raison sociale
	siren: string;
	siret: string;					// établissement retenu (le siège à défaut de mieux)
	city: string;
	naf: string;						// activité principale — aide à reconnaître le bon établissement
	idcc: string[];					// numéros IDCC déclarés (souvent un seul, parfois aucun)
	conventions: IdccEntry[];		// ceux que le catalogue KALI sait nommer
}

const API = 'https://recherche-entreprises.api.gouv.fr/search';

@Injectable({ providedIn: 'root' })
export class HrIdccService {

	// Client SANS intercepteurs — cf. l'en-tête du fichier.
	private http = new HttpClient(inject(HttpBackend));

	// Recherche par SIRET, SIREN ou raison sociale. Rend [] sur toute erreur : l'annuaire est un
	// CONFORT, jamais un passage obligé — la saisie manuelle de l'IDCC reste possible en toutes
	// circonstances, y compris hors ligne.
	searchCompany$(query: string): Observable<CompanyIdccMatch[]> {
		const q = (query ?? '').trim();
		if (q.length < 3) return of([]);
		const url = API + '?q=' + encodeURIComponent(q) + '&per_page=10&limite_matching_etablissements=3';
		return this.http.get<any>(url).pipe(
			map(res => (res?.results ?? []).map((r: any) => this.matchOf(r)).filter((m: CompanyIdccMatch) => !!m.siren)),
			catchError(() => of([] as CompanyIdccMatch[])),
		);
	}

	private matchOf(r: any): CompanyIdccMatch {
		// Les IDCC vivent au niveau ÉTABLISSEMENT : deux établissements d'une même société peuvent
		// relever de conventions différentes. On rassemble ceux des établissements correspondants
		// et du siège, en dédoublonnant.
		const etabs: any[] = [...(r?.matching_etablissements ?? []), r?.siege].filter(Boolean);
		const idcc = Array.from(new Set(
			etabs.flatMap(e => (e?.liste_idcc ?? []) as string[])
				.map(v => (v ?? '').toString().trim().replace(/^0+/, ''))
				.filter(v => v && v !== '9999'),		// 9999 = « sans convention collective »
		));
		const first = etabs[0] ?? {};
		return {
			name: r?.nom_complet ?? r?.nom_raison_sociale ?? '',
			siren: r?.siren ?? '',
			siret: first?.siret ?? '',
			city: first?.libelle_commune ?? first?.commune ?? '',
			naf: r?.siege?.activite_principale ?? first?.activite_principale ?? '',
			idcc,
			conventions: idcc.map(n => hrIdccOf(n)).filter((e): e is IdccEntry => !!e),
		};
	}
}
